export const extractVideoFrames = async (
    file: File,
    frameCount: number = 5,
    quality: number = 0.8,
): Promise<Blob[]> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
        }

        const frames: Blob[] = [];
        let currentFrame = 0;

        video.addEventListener('loadedmetadata', () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const duration = video.duration;
            const interval = duration / (frameCount + 1);

            extractFrameAtTime(0);
        });

        video.addEventListener('seeked', () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        frames.push(blob);
                        currentFrame++;

                        if (currentFrame < frameCount) {
                            const nextTime =
                                (currentFrame + 1) * (video.duration / (frameCount + 1));
                            video.currentTime = nextTime;
                        } else {
                            resolve(frames);
                        }
                    } else {
                        reject(new Error('Failed to create blob from canvas'));
                    }
                },
                'image/jpeg',
                quality,
            );
        });

        video.addEventListener('error', (e) => {
            reject(new Error(`Video loading error: ${e}`));
        });

        const extractFrameAtTime = (time: number) => {
            video.currentTime = time;
        };

        video.src = URL.createObjectURL(file);
        video.load();
    });
};

export const extractVideoFramesByInterval = async (
    file: File,
    intervalSeconds: number = 1,
    quality: number = 0.8,
): Promise<Blob[]> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
        }

        const frames: Blob[] = [];
        let currentTime = 0;

        video.addEventListener('loadedmetadata', () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const duration = video.duration;
            const frameCount = Math.floor(duration / intervalSeconds);

            if (frameCount === 0) {
                resolve([]);
                return;
            }

            extractNextFrame();
        });

        video.addEventListener('seeked', () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        frames.push(blob);
                        currentTime += intervalSeconds;

                        if (currentTime < video.duration) {
                            extractNextFrame();
                        } else {
                            resolve(frames);
                        }
                    } else {
                        reject(new Error('Failed to create blob from canvas'));
                    }
                },
                'image/jpeg',
                quality,
            );
        });

        video.addEventListener('error', (e) => {
            reject(new Error(`Video loading error: ${e}`));
        });

        const extractNextFrame = () => {
            video.currentTime = currentTime;
        };

        video.src = URL.createObjectURL(file);
        video.load();
    });
};

export const extractVideoFrameAtTime = async (
    file: File,
    timeSeconds: number,
    quality: number = 0.8,
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
        }

        video.addEventListener('loadedmetadata', () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            if (timeSeconds > video.duration) {
                reject(new Error('Time exceeds video duration'));
                return;
            }

            video.currentTime = timeSeconds;
        });

        video.addEventListener('seeked', () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to create blob from canvas'));
                    }
                },
                'image/jpeg',
                quality,
            );
        });

        video.addEventListener('error', (e) => {
            reject(new Error(`Video loading error: ${e}`));
        });

        video.src = URL.createObjectURL(file);
        video.load();
    });
};
